import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, resolveAvatar, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Send, MessageSquare, ShieldCheck, ArrowLeft, ImagePlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const Messages = () => {
  const { user, loading: authLoading, requireAction, login } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // other_id
  const [other, setOther] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const sendMedia = async (file) => {
    if (!file || !active) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to_id", active);
      fd.append("file", file);
      fd.append("text", text.trim());
      await api.post("/messages/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText("");
      await loadThread(active); loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send media"); }
    finally { setSending(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const loadThreads = useCallback(async () => {
    try { const r = await api.get("/messages/threads"); setThreads(r.data); } catch {}
  }, []);

  const loadThread = useCallback(async (otherId) => {
    if (!otherId) return;
    try {
      const r = await api.get(`/messages/with/${otherId}`);
      setMsgs(r.data.messages);
      setOther(r.data.other);
    } catch {}
  }, []);

  // initial: handle ?to=owner or ?to=<id>
  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    const boot = async () => {
      setLoading(true);
      await loadThreads();
      const to = params.get("to");
      if (to === "owner") {
        try { const r = await api.get("/community/owner"); setActive(r.data.user_id); }
        catch { toast.error("Owner unavailable"); }
      } else if (to) {
        setActive(to);
      }
      setLoading(false);
    };
    boot();
  }, [user, authLoading]); // eslint-disable-line

  // load active thread + poll
  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const id = setInterval(async () => { await loadThread(active); loadThreads(); }, 3000);
    return () => clearInterval(id);
  }, [active, loadThread, loadThreads]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const openThread = (otherId) => {
    setActive(otherId);
    setParams({ to: otherId });
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    if (!requireAction()) return;
    setSending(true);
    // optimistic
    const optim = { id: `tmp_${Date.now()}`, from_id: user.user_id, to_id: active, text: t, created_at: new Date().toISOString(), _optim: true };
    setMsgs((m) => [...m, optim]);
    setText("");
    try {
      await api.post("/messages", { to_id: active, text: t });
      await loadThread(active);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
      setMsgs((m) => m.filter((x) => x.id !== optim.id));
    } finally { setSending(false); }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen relative z-10">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-32 px-6">
          <MessageSquare className="w-10 h-10 mx-auto text-primary mb-4" />
          <h1 className="font-heading text-3xl mb-3">Messages are for members</h1>
          <p className="text-muted-foreground mb-6">Sign in to message the owner and other members.</p>
          <Button data-testid="messages-signin" onClick={login} className="rounded-full">Sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6">
        <div className="flex items-center gap-2 text-primary mb-1"><MessageSquare className="w-4 h-4" />
          <span className="text-xs tracking-[0.2em] uppercase font-bold">Direct Messages</span></div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter mb-6">Inbox</h1>

        <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[460px]">
          {/* thread list */}
          <div data-testid="thread-list" className={cn("rounded-xl border border-border bg-card overflow-y-auto", active && "hidden md:block")}>
            {!user?.is_admin && (
            <button data-testid="message-owner-btn" onClick={async () => {
              try { const r = await api.get("/community/owner"); openThread(r.data.user_id); } catch { toast.error("Owner unavailable"); }
            }} className="w-full flex items-center gap-3 p-4 border-b border-border hover:bg-secondary/40 transition-colors text-left">
              <span className="w-10 h-10 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-primary" /></span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-primary">Message the owner</div>
                <div className="text-xs text-muted-foreground truncate">Suggest video types & improvements</div>
              </div>
            </button>
            )}
            {threads.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No conversations yet.</div>
            ) : threads.map((t) => (
              <button key={t.other_id} data-testid={`thread-${t.other_id}`} onClick={() => openThread(t.other_id)}
                className={cn("w-full flex items-center gap-3 p-4 border-b border-border/60 hover:bg-secondary/40 transition-colors text-left", active === t.other_id && "bg-secondary/60")}>
                <Avatar className="w-10 h-10 border border-border shrink-0"><AvatarImage src={resolveAvatar(t.other_avatar)} /><AvatarFallback className="bg-secondary text-xs">{t.other_name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium truncate">{t.other_name}</span>
                    {t.other_is_admin && <ShieldCheck className="w-3 h-3 text-primary shrink-0" />}
                    <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{timeAgo(t.last_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn("text-xs truncate", t.unread ? "text-foreground font-medium" : "text-muted-foreground")}>{t.last_from_me ? "You: " : ""}{t.last_text}</span>
                    {t.unread > 0 && <span className="ml-auto text-[10px] font-bold bg-primary text-primary-foreground rounded-full px-1.5 shrink-0">{t.unread}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* conversation */}
          <div className={cn("rounded-xl border border-border bg-card flex flex-col overflow-hidden", !active && "hidden md:flex")}>
            {!active ? (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
                <MessageSquare className="w-10 h-10 opacity-40" />
                <p className="text-sm">Select a conversation, or message the owner.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-4 border-b border-border">
                  <button onClick={() => { setActive(null); setParams({}); }} className="md:hidden text-muted-foreground"><ArrowLeft className="w-5 h-5" /></button>
                  <Avatar className="w-9 h-9 border border-border cursor-pointer" onClick={() => navigate(`/u/${active}`)}><AvatarImage src={resolveAvatar(other?.avatar_url)} /><AvatarFallback className="bg-secondary text-xs">{other?.name?.[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-1.5">{other?.name || "Member"}{other?.is_admin && <span className="text-[9px] uppercase font-bold text-primary border border-primary/40 rounded px-1">Owner</span>}</div>
                    <div className="text-[11px] text-muted-foreground">Live · updates every few seconds</div>
                  </div>
                </div>

                <div ref={scrollRef} data-testid="message-scroll" className="flex-1 overflow-y-auto p-4 space-y-3">
                  {msgs.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Say hello 👋</div>
                    : msgs.map((m) => {
                    const mine = m.from_id === user.user_id;
                    return (
                      <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                        <div className={cn("max-w-[75%] rounded-2xl px-4 py-2 text-sm", mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm")}>
                          {m.attachment_path && (
                            <div className="mb-1.5 -mx-1 rounded-xl overflow-hidden w-52 max-w-full">
                              {m.attachment_type === "video"
                                ? <video src={fileUrl(m.attachment_path)} controls className="w-52 max-w-full max-h-72 rounded-xl bg-black" />
                                : <img src={fileUrl(m.attachment_path)} alt="attachment" className="w-52 max-w-full max-h-72 object-cover rounded-xl bg-black" />}
                            </div>
                          )}
                          {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                          <div className={cn("text-[10px] mt-1 opacity-60", mine ? "text-right" : "")}>{timeAgo(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="p-3 border-t border-border flex items-end gap-2">
                  {user?.is_admin && (
                    <>
                      <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={(e) => sendMedia(e.target.files?.[0])} data-testid="dm-attach-input" />
                      <Button data-testid="dm-attach-btn" onClick={() => fileRef.current?.click()} disabled={sending} variant="outline" className="rounded-full h-11 w-11 p-0 shrink-0 border-border" title="Send photo or video">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                      </Button>
                    </>
                  )}
                  <textarea data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={1} placeholder="Write a message…"
                    className="flex-1 resize-none bg-secondary/50 border border-border rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-primary/50 max-h-32" />
                  <Button data-testid="message-send" onClick={send} disabled={sending || !text.trim()} className="rounded-full h-11 w-11 p-0 shrink-0">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
